<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import PatientProfilesAPI from 'dashboard/api/patientProfiles.js';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PatientProfileFormFields from 'dashboard/components-next/Contacts/PatientProfileFormFields.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const profile = ref(null);
const loading = ref(false);
const error = ref(null);

const dialogRef = ref(null);
const clinicalFieldsRef = ref(null);
const clinicalData = ref({});
const isSaving = ref(false);

const phaseLabels = {
  pre_op: 'Pré-op',
  surgery: 'Cirurgia',
  post_op_phase_1: 'Pós-op Fase 1',
  post_op_phase_2: 'Pós-op Fase 2',
  discharged: 'Alta',
};

const phaseColors = {
  pre_op: 'bg-n-blue-9 text-white',
  surgery: 'bg-n-violet-9 text-white',
  post_op_phase_1: 'bg-n-amber-9 text-white',
  post_op_phase_2: 'bg-n-emerald-9 text-white',
  discharged: 'bg-n-slate-9 text-white',
};

const genderLabels = { M: 'Masculino', F: 'Feminino', O: 'Outro' };

async function load() {
  if (!props.contactId) return;
  loading.value = true;
  error.value = null;
  try {
    // Acha o profile.id via list (filtrado) e depois faz show pra ter todos os campos
    const { data: listData } = await PatientProfilesAPI.list({ perPage: 200 });
    const summary = (listData.data || []).find(
      p => p.contact_id === Number(props.contactId)
    );
    if (!summary) {
      profile.value = null;
      return;
    }
    const { data: detailData } = await PatientProfilesAPI.show(summary.id);
    profile.value = detailData.data;
  } catch (e) {
    error.value = e?.response?.data?.message || e.message;
  } finally {
    loading.value = false;
  }
}

const hasAnyList = computed(() => {
  if (!profile.value) return false;
  return (
    (profile.value.previous_diseases || []).length > 0 ||
    (profile.value.current_medications || []).length > 0 ||
    (profile.value.other_exams || []).length > 0
  );
});

// Mapeia o profile carregado (serializer do show) -> chaves esperadas pelo form.
// O serializer devolve procedure/digital_human/physician aninhados ({ id, name }),
// mas o form usa as chaves planas *_id; os demais campos batem direto.
const initialValues = computed(() => {
  const p = profile.value;
  if (!p) return {};
  const values = {};
  const assign = (key, value) => {
    if (value !== null && value !== undefined) values[key] = value;
  };
  assign('procedure_id', p.procedure?.id);
  assign('assigned_physician_id', p.physician?.id);
  assign('assigned_digital_human_id', p.digital_human?.id);
  assign('surgery_date', p.surgery_date);
  assign('journey_phase', p.journey_phase);
  assign('medical_record_number', p.medical_record_number);
  assign('birth_date', p.birth_date);
  assign('gender', p.gender);
  assign('height_cm', p.height_cm);
  assign('weight_kg', p.weight_kg);
  assign('blood_type', p.blood_type);
  assign('allergies', p.allergies);
  assign('physician_ids', p.physician_ids);
  assign('last_consultation', p.last_consultation);
  assign('primary_diagnosis', p.primary_diagnosis);
  assign('previous_diseases', p.previous_diseases);
  assign('current_medications', p.current_medications);
  assign('other_exams', p.other_exams);
  assign('exam_results', p.exam_results);
  assign('program_name', p.program_name);
  assign('subcategory_name', p.subcategory_name);
  assign('care_template_name', p.care_template_name);
  assign('care_description', p.care_description);
  assign('notes', p.notes);
  assign('medical_guidance', p.medical_guidance);
  return values;
});

const dialogTitle = computed(() =>
  profile.value ? 'Editar dados clínicos' : 'Cadastrar dados clínicos'
);

const handleClinicalChange = data => {
  clinicalData.value = data;
};

const openDialog = () => {
  dialogRef.value?.open();
};

// Monta o payload plano só com os campos preenchidos (mesma lógica do
// CreateNewContactDialog), ignorando strings vazias, null/undefined e
// arrays vazios para não sobrescrever com valores em branco.
// Lê via getFormData() (fonte da verdade) com fallback no @change, pois o
// watcher do form só emite após uma alteração — salvar sem editar nada
// deixaria clinicalData vazio.
const buildPayload = () => {
  const source = clinicalFieldsRef.value?.getFormData?.() || clinicalData.value;
  const payload = {};
  Object.entries(source).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    if (Array.isArray(value) && value.length === 0) return;
    payload[key] = value;
  });
  return payload;
};

const handleSave = async () => {
  isSaving.value = true;
  try {
    const data = buildPayload();
    if (profile.value) {
      await PatientProfilesAPI.update(profile.value.id, data);
      useAlert('Dados clínicos atualizados com sucesso');
    } else {
      await PatientProfilesAPI.create({ contact_id: props.contactId, ...data });
      useAlert('Dados clínicos salvos com sucesso');
    }
    dialogRef.value?.close();
    await load();
  } catch (e) {
    useAlert(
      `Falha ao salvar dados clínicos: ${e?.response?.data?.error || e.message}`
    );
  } finally {
    isSaving.value = false;
  }
};

watch(() => props.contactId, load);
onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <section class="flex flex-col w-full gap-4 pt-6 border-t border-n-strong">
    <div class="flex items-center justify-between">
      <h6 class="text-base font-medium text-n-slate-12">Dados clínicos</h6>
      <div class="flex items-center gap-3">
        <button
          v-if="!loading"
          class="text-xs text-n-brand hover:underline"
          @click="load"
        >
          atualizar
        </button>
        <Button
          v-if="!loading && !error"
          :label="profile ? 'Editar' : 'Cadastrar dados clínicos'"
          size="sm"
          color="blue"
          @click="openDialog"
        />
      </div>
    </div>

    <div v-if="loading" class="text-sm text-n-slate-11">Carregando…</div>

    <div v-else-if="error" class="text-sm text-n-ruby-11">
      Erro: {{ error }}
    </div>

    <div
      v-else-if="!profile"
      class="bg-n-solid-2 border border-n-weak rounded-lg p-3"
    >
      <p class="text-sm text-n-slate-11 mb-2">
        Este contato ainda não tem perfil clínico.
      </p>
      <p class="text-xs text-n-slate-10">
        Use “Cadastrar dados clínicos” para registrar este contato como
        paciente.
      </p>
    </div>

    <div v-else class="flex flex-col gap-4 text-sm">
      <!-- Status / Fase -->
      <div class="flex items-center gap-2 flex-wrap">
        <span
          class="text-xs font-bold px-2 py-0.5 rounded-md"
          :class="phaseColors[profile.journey_phase]"
        >
          {{ phaseLabels[profile.journey_phase] }}
        </span>
        <span
          v-if="profile.post_op_day !== null"
          class="text-xs text-n-slate-10"
        >
          POD {{ profile.post_op_day }} · modo: {{ profile.classifier_mode }}
        </span>
        <span
          v-if="profile.open_alerts_count > 0"
          class="text-xs font-bold px-2 py-0.5 rounded-md bg-n-ruby-9 text-white"
        >
          {{ profile.open_alerts_count }} alerta(s) abertos
        </span>
      </div>

      <!-- Identificação -->
      <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <template v-if="profile.medical_record_number">
          <dt class="text-n-slate-11">Prontuário</dt>
          <dd class="text-n-slate-12 font-medium">
            {{ profile.medical_record_number }}
          </dd>
        </template>
        <template v-if="profile.birth_date">
          <dt class="text-n-slate-11">Nascimento</dt>
          <dd class="text-n-slate-12 font-medium">{{ profile.birth_date }}</dd>
        </template>
        <template v-if="profile.gender">
          <dt class="text-n-slate-11">Sexo</dt>
          <dd class="text-n-slate-12 font-medium">
            {{ genderLabels[profile.gender] || profile.gender }}
          </dd>
        </template>
        <template v-if="profile.procedure">
          <dt class="text-n-slate-11">Procedimento</dt>
          <dd class="text-n-slate-12 font-medium">
            {{ profile.procedure.name }}
          </dd>
        </template>
        <template v-if="profile.surgery_date">
          <dt class="text-n-slate-11">Data da cirurgia</dt>
          <dd class="text-n-slate-12 font-medium">
            {{ profile.surgery_date }}
          </dd>
        </template>
        <template v-if="profile.last_consultation">
          <dt class="text-n-slate-11">Última consulta</dt>
          <dd class="text-n-slate-12 font-medium">
            {{ profile.last_consultation }}
          </dd>
        </template>
        <template v-if="profile.physician">
          <dt class="text-n-slate-11">Médico responsável</dt>
          <dd class="text-n-slate-12 font-medium">
            {{ profile.physician.name }}
          </dd>
        </template>
        <template v-if="profile.digital_human">
          <dt class="text-n-slate-11">Humano digital</dt>
          <dd class="text-n-slate-12 font-medium">
            {{ profile.digital_human.name }}
          </dd>
        </template>
      </dl>

      <!-- Programa -->
      <div
        v-if="
          profile.program_name ||
          profile.subcategory_name ||
          profile.care_template_name
        "
        class="bg-n-solid-2 rounded-md p-3 text-xs"
      >
        <div v-if="profile.program_name" class="mb-1">
          <span class="text-n-slate-11">Programa:</span>
          <span class="text-n-slate-12 font-medium ml-1">{{
            profile.program_name
          }}</span>
          <span v-if="profile.subcategory_name" class="text-n-slate-11">
            /
          </span>
          <span
            v-if="profile.subcategory_name"
            class="text-n-slate-12 font-medium"
          >
            {{ profile.subcategory_name }}
          </span>
        </div>
        <div v-if="profile.care_template_name">
          <span class="text-n-slate-11">Modelo de cuidado:</span>
          <span class="text-n-slate-12 font-medium ml-1">{{
            profile.care_template_name
          }}</span>
        </div>
      </div>

      <!-- Diagnóstico -->
      <div v-if="profile.primary_diagnosis">
        <h6
          class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
        >
          Diagnóstico principal
        </h6>
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
          {{ profile.primary_diagnosis }}
        </p>
      </div>

      <!-- Listas: doenças, medicações, exames -->
      <div v-if="hasAnyList" class="grid grid-cols-1 gap-3">
        <div v-if="profile.previous_diseases?.length">
          <h6
            class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
          >
            Doenças prévias
          </h6>
          <ul class="flex flex-wrap gap-1">
            <li
              v-for="(d, i) in profile.previous_diseases"
              :key="`d-${i}`"
              class="text-xs bg-n-solid-2 border border-n-weak rounded-md px-2 py-0.5"
            >
              {{ d }}
            </li>
          </ul>
        </div>

        <div v-if="profile.current_medications?.length">
          <h6
            class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
          >
            Medicações em uso
          </h6>
          <ul class="flex flex-wrap gap-1">
            <li
              v-for="(m, i) in profile.current_medications"
              :key="`m-${i}`"
              class="text-xs bg-n-solid-2 border border-n-weak rounded-md px-2 py-0.5"
            >
              {{ m }}
            </li>
          </ul>
        </div>

        <div v-if="profile.other_exams?.length">
          <h6
            class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
          >
            Outros exames
          </h6>
          <ul class="flex flex-wrap gap-1">
            <li
              v-for="(e, i) in profile.other_exams"
              :key="`e-${i}`"
              class="text-xs bg-n-solid-2 border border-n-weak rounded-md px-2 py-0.5"
            >
              {{ e }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Exames detalhados -->
      <div v-if="profile.exam_results">
        <h6
          class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
        >
          Resultados de exames
        </h6>
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
          {{ profile.exam_results }}
        </p>
      </div>

      <!-- Acompanhamento -->
      <div v-if="profile.care_description">
        <h6
          class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
        >
          Descrição do acompanhamento
        </h6>
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
          {{ profile.care_description }}
        </p>
      </div>

      <div v-if="profile.medical_guidance">
        <h6
          class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
        >
          Orientações médicas
        </h6>
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
          {{ profile.medical_guidance }}
        </p>
      </div>

      <div v-if="profile.notes">
        <h6
          class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
        >
          Observações gerais
        </h6>
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
          {{ profile.notes }}
        </p>
      </div>
    </div>

    <Dialog ref="dialogRef" width="3xl" overflow-y-auto @confirm="handleSave">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        {{ dialogTitle }}
      </h3>
      <PatientProfileFormFields
        :key="profile?.id || 'new'"
        ref="clinicalFieldsRef"
        :initial-values="initialValues"
        @change="handleClinicalChange"
      />
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            label="Cancelar"
            variant="link"
            type="reset"
            class="h-10 hover:!no-underline hover:text-n-brand"
            @click="dialogRef?.close()"
          />
          <Button
            type="submit"
            label="Salvar"
            color="blue"
            :is-loading="isSaving"
          />
        </div>
      </template>
    </Dialog>
  </section>
</template>
